import { useMemo } from 'react';
import PropTypes from 'prop-types';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider as MUIThemeProvider } from '@mui/material/styles';

export const eToolsPalette = {
  primary: {
    lighter: '#CCFBF1',
    light: '#5EEAD4',
    main: '#0D9488',
    dark: '#0F766E',
    darker: '#134E4A',
    contrastText: '#FFFFFF',
  },
  secondary: {
    lighter: '#FEF3C7',
    light: '#FCD34D',
    main: '#F59E0B',
    dark: '#D97706',
    darker: '#92400E',
    contrastText: '#FFFFFF',
  },
  success: {
    lighter: '#D1FAE5',
    light: '#6EE7B7',
    main: '#10B981',
    dark: '#047857',
    darker: '#064E3B',
    contrastText: '#FFFFFF',
  },
  warning: {
    lighter: '#FEF3C7',
    light: '#FCD34D',
    main: '#F97316',
    dark: '#C2410C',
    darker: '#7C2D12',
    contrastText: '#FFFFFF',
  },
  error: {
    lighter: '#FEE2E2',
    light: '#FCA5A5',
    main: '#EF4444',
    dark: '#B91C1C',
    darker: '#7F1D1D',
    contrastText: '#FFFFFF',
  },
  info: {
    lighter: '#DBEAFE',
    light: '#93C5FD',
    main: '#3B82F6',
    dark: '#1D4ED8',
    darker: '#1E3A8A',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#F8FAFC',
    paper: '#FFFFFF',
    neutral: '#F1F5F9',
  },
  text: {
    primary: '#0F172A',
    secondary: '#475569',
    disabled: '#94A3B8',
  },
  divider: '#E2E8F0',
};

export default function EToolsThemeProvider({ children }) {
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          ...eToolsPalette,
        },
        shape: { borderRadius: 10 },
        typography: {
          fontFamily:
            '"Inter", "Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          h1: { fontWeight: 800, letterSpacing: '-0.02em' },
          h2: { fontWeight: 800, letterSpacing: '-0.02em' },
          h3: { fontWeight: 700, letterSpacing: '-0.01em' },
          h4: { fontWeight: 700, letterSpacing: '-0.01em' },
          h5: { fontWeight: 700 },
          h6: { fontWeight: 700 },
          button: { fontWeight: 600, textTransform: 'none' },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: { borderRadius: 8, textTransform: 'none', fontWeight: 600 },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: { backgroundImage: 'none' },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
                border: '1px solid #E2E8F0',
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: { boxShadow: 'none' },
            },
          },
        },
      }),
    []
  );

  return (
    <MUIThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MUIThemeProvider>
  );
}

EToolsThemeProvider.propTypes = {
  children: PropTypes.node,
};
